import * as csstree from '../vendor/csstree.esm.js';

const OAK_HARBOR_SITE = 'oakharborwebdesigns.com';
const OAK_HARBOR_SAFE_SITE = 'oakharborwebdesigns-com';

// ── dark-mode toggle anchor corrective ───────────────────────────────────
//
// global-base ships an unconditional mobile default for the theme toggle:
//   [id^="dark-mode-toggle"]{position:absolute;top:50%;transform:translateY(-50%);…}
// The crawled originals contain NO such rule — it is ours, a fallback for
// templates that never position the toggle. But CSS has no "unless the
// template positioned it" — a template that anchors with `bottom` and never
// declares `top` (affordablesolar `bottom:.625rem`, uicompass `bottom:.8rem`)
// gets BOTH: top+bottom+height over-constrains the box, `bottom` goes dead,
// and the toggle floats to the header's vertical center with a stray
// translateY(-50%). Measured: uicompass 20px high vs its own original (which
// computes transform:none), affordablesolar 33px.
//
// The corrective: for a section whose own CSS declares `bottom` on a
// toggle-ROOT selector without declaring `top`, emit a scoped
// `top:auto` (+ `transform:none` when the template declares no toggle
// transform of its own) so the template's declared anchor wins. Scoped
// [data-pb-section]/#exportId selectors out-rank the base's (0,1,0) attribute
// selector without !important, and the predicate is per-property, so a
// template that DOES declare `top` (wholesalewindows `top:0rem`) or its own
// transform is never touched. Detection is css-tree AST over rightmost
// compounds — never regex over selectors (repo rule) — so `#dark-mode-toggle
// .cs-sun{transform:…}` (icon animation on a CHILD) does not read as the
// toggle owning `transform`.

const TOGGLE_ID_PREFIX = 'dark-mode-toggle';
const TOP_PROPS = new Set(['top', 'inset', 'inset-block', 'inset-block-start']);
const BOTTOM_PROPS = new Set(['bottom', 'inset', 'inset-block', 'inset-block-end']);
const TRANSFORM_PROPS = new Set(['transform', 'translate']);

// The base centering applies from width 0 and is reset by the base's own
// desktop rule at 64rem, so the defect window is sub-64rem. A template
// declaration confined to a >=64rem media block cannot own that window —
// counting it suppressed the transform half of the corrective on every
// template with a desktop `transform:none` reset (all three measured ones).
const DESKTOP_EDGE_REM = 64;

/** Media prelude confines its rules to >= 64rem (desktop-only)? */
function mediaIsDesktopOnly(preludeText) {
  const match = /min-width\s*:\s*([\d.]+)\s*(rem|em|px)/i.exec(String(preludeText || ''));
  if (!match) return false;
  const value = parseFloat(match[1]);
  if (!Number.isFinite(value)) return false;
  const rem = match[2].toLowerCase() === 'px' ? value / 16 : value;
  return rem >= DESKTOP_EDGE_REM;
}

function unquote(value) {
  return String(value || '').replace(/^["']|["']$/g, '');
}

/** Does this compound (array of simple-selector nodes) target the toggle itself? */
function compoundTargetsToggle(compound) {
  return compound.some((node) => {
    if (node.type === 'IdSelector') return String(node.name || '').startsWith(TOGGLE_ID_PREFIX);
    if (node.type === 'AttributeSelector') {
      const attr = node.name && (node.name.name || node.name);
      if (String(attr).toLowerCase() !== 'id') return false;
      const value = unquote(node.value && (node.value.value ?? node.value.name));
      return value.startsWith(TOGGLE_ID_PREFIX) || TOGGLE_ID_PREFIX.startsWith(value);
    }
    return false;
  });
}

/** Rightmost compound of one Selector node. */
function rightmostCompound(selectorNode) {
  const compound = [];
  let current = [];
  selectorNode.children.forEach((child) => {
    if (child.type === 'Combinator') {
      current = [];
    } else {
      current.push(child);
    }
  });
  compound.push(...current);
  return compound;
}

/**
 * Anchor analysis of one section's CSS: which vertical/transform properties
 * does the template itself declare on toggle-root selectors?
 */
export function analyzeToggleAnchor(cssText) {
  const result = { hasToggleRule: false, top: false, bottom: false, transform: false };
  const css = String(cssText || '');
  if (!css.includes(TOGGLE_ID_PREFIX)) return result;
  let ast;
  try {
    ast = csstree.parse(css, { parseValue: false });
  } catch {
    return result;
  }
  csstree.walk(ast, {
    visit: 'Rule',
    enter(rule) {
      if (!rule.prelude || rule.prelude.type !== 'SelectorList') return;
      // Desktop-only rules cannot own the sub-64rem window the base rule breaks.
      const atrule = this.atrule;
      if (
        atrule &&
        String(atrule.name).toLowerCase() === 'media' &&
        mediaIsDesktopOnly(atrule.prelude ? csstree.generate(atrule.prelude) : '')
      ) {
        return;
      }
      let targets = false;
      rule.prelude.children.forEach((selector) => {
        if (selector.type === 'Selector' && compoundTargetsToggle(rightmostCompound(selector))) {
          targets = true;
        }
      });
      if (!targets) return;
      result.hasToggleRule = true;
      csstree.walk(rule.block, {
        visit: 'Declaration',
        enter(decl) {
          const prop = String(decl.property || '').toLowerCase();
          if (TOP_PROPS.has(prop)) result.top = true;
          if (BOTTOM_PROPS.has(prop)) result.bottom = true;
          if (TRANSFORM_PROPS.has(prop)) result.transform = true;
        }
      });
    }
  });
  return result;
}

function escapeCSSString(value) {
  return String(value == null ? '' : value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\a ')
    .replace(/\r/g, '');
}

function defaultSectionSelector(section) {
  const id = section?.sectionId;
  return id ? `[data-pb-section="${escapeCSSString(id)}"]` : '';
}

function isOakHarborSection(section, templateId) {
  if (!section || typeof section !== 'object') return false;
  const sectionId = String(section.sectionId || '');
  const catalogKey = String(section.catalogKey || '');
  const sourceTemplateId = String(section.templateSectionId || '');
  const siteName = String(section.templateSiteName || '');
  const namespacedId = `tpl-${OAK_HARBOR_SAFE_SITE}-${templateId}`;
  const templateCatalogKey = `template-${OAK_HARBOR_SITE}-${templateId}`;

  return (
    sectionId === templateId ||
    sectionId === namespacedId ||
    catalogKey === templateCatalogKey ||
    (siteName === OAK_HARBOR_SITE && sourceTemplateId === templateId)
  );
}

function selectorList(selectors, suffix = '') {
  return selectors.map((selector) => `${selector}${suffix}`).join(',');
}

export function assembleTemplateFidelityCSS(
  sections,
  { selectorForSection = defaultSectionSelector } = {}
) {
  if (!Array.isArray(sections) || sections.length === 0) return '';

  const reviewSelectors = new Set();
  const footerSelectors = new Set();
  // Buckets keyed by which correction the section needs (see analysis above).
  const toggleTopOnly = new Set();
  const toggleTopAndTransform = new Set();

  for (const section of sections) {
    const selector = selectorForSection(section);
    if (!selector) continue;
    if (isOakHarborSection(section, 'reviews-355')) reviewSelectors.add(selector);
    if (isOakHarborSection(section, 'cs-footer-840')) footerSelectors.add(selector);
    if (section.css && String(section.css).includes(TOGGLE_ID_PREFIX)) {
      const anchor = analyzeToggleAnchor(section.css);
      if (anchor.bottom && !anchor.top) {
        (anchor.transform ? toggleTopOnly : toggleTopAndTransform).add(selector);
      }
    }
  }

  const blocks = [];

  if (toggleTopAndTransform.size || toggleTopOnly.size) {
    const parts = [
      '/* Toggle anchor corrective: the base layer centers [id^="dark-mode-toggle"]',
      '   with top:50%/translateY(-50%); these templates anchor it with `bottom` and',
      '   never declare `top`, so the over-constrained box floated to header center.',
      '   Scoped so the template-declared anchor wins; per-property so templates',
      '   declaring their own top/transform are untouched. */'
    ];
    if (toggleTopAndTransform.size) {
      parts.push(
        `${selectorList(Array.from(toggleTopAndTransform), ` [id^="${TOGGLE_ID_PREFIX}"]`)} {
  top: auto;
  transform: none;
}`
      );
    }
    if (toggleTopOnly.size) {
      parts.push(
        `${selectorList(Array.from(toggleTopOnly), ` [id^="${TOGGLE_ID_PREFIX}"]`)} {
  top: auto;
}`
      );
    }
    blocks.push(parts.join('\n'));
  }

  if (reviewSelectors.size) {
    const reviews = Array.from(reviewSelectors);
    blocks.push(`
${selectorList(reviews, ' .cs-bubbles')} {
  left: -16.25em !important;
  right: auto !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

@media only screen and (min-width: 100rem) {
  ${selectorList(reviews, ' .cs-bubbles')} {
    bottom: 1.875rem !important;
    left: -16.25em !important;
    right: auto !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}`);
  }

  if (footerSelectors.size) {
    const footers = Array.from(footerSelectors);
    blocks.push(`
${selectorList(footers)} {
  padding-top: clamp(6.25rem, 20vw, 18.5rem) !important;
}

${selectorList(footers, ' .cs-footer-swoosh')} {
  display: block !important;
}

${selectorList(footers, ' .cs-logo')} {
  width: 19.625rem !important;
  max-width: 55vw !important;
}

${selectorList(footers, ' .cs-logo .logo-slot')} {
  display: block !important;
  width: 100% !important;
  height: auto !important;
  aspect-ratio: 1507 / 470 !important;
}

${selectorList(footers, ' .cs-logo .logo-slot > img')} {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  object-position: left center !important;
}

${selectorList(footers, ' .cs-logo-img')} {
  width: 100% !important;
  height: 100% !important;
}`);
  }

  return blocks.join('\n\n').trim();
}
